import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro } from "@/admin/ui";
import { GroupBadge } from "@/components/GroupBadge";
import { initials } from "@/lib/format";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import {
  Search, UserPlus, Star, Check, X, Plane, BedDouble, Car, CalendarCheck,
  Utensils, ShieldCheck, Ban, Plus, Wallet, Award, Shirt, Eye, Download,
} from "lucide-react";
import { toast } from "sonner";

const GROUP_OPTS = [
  ["all", "All groups"], ["mdc_winner", "MDC Winner"], ["pc_winner", "PC Winner"],
  ["sprint_to_pc", "Sprint to PC"], ["non_pc", "Non-PC"], ["leadership", "Leadership"],
];
const RSVP_OPTS = [["all", "All RSVP"], ["attending", "Attending"], ["pending", "Pending"], ["declined", "Declined"]];
const PLUS_OPTS = [["all", "All"], ["yes", "Has plus-one"], ["none", "No plus-one"]];

// Manager-only T-Shirt operational buckets (never shown to attendees).
const TSHIRT_ORDER = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "NA", "MISSING"];
const tshirtKey = (v) => {
  const s = (v == null ? "" : String(v)).trim().toUpperCase();
  if (!s) return "MISSING";
  return TSHIRT_ORDER.includes(s) ? s : "MISSING";
};

export default function Attendees() {
  const { eventId } = useAdminEvent();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [group, setGroup] = useState("all");
  const [rsvp, setRsvp] = useState("all");
  const [plus, setPlus] = useState("all");
  const [tshirt, setTshirt] = useState("all");
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [loadState, setLoadState] = useState("loading"); // loading | ok | error
  const [exporting, setExporting] = useState(false);
  const [exportInfo, setExportInfo] = useState(null); // { count, ts, filename } | { error }

  const doExport = async () => {
    if (!eventId || exporting) return;
    setExporting(true); setExportInfo(null);
    try {
      const res = await api.adminAttendeesExport(eventId);
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `${eventId}_Roster.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      let count = res.headers?.["x-export-count"];
      if (count == null) {
        const text = await blob.text();
        count = Math.max(0, text.trim().split("\n").length - 1);
      }
      const ts = res.headers?.["x-export-generated-at"] || new Date().toISOString();
      setExportInfo({ count: Number(count), ts, filename });
    } catch (e) {
      setExportInfo({ error: e?.response?.status === 403 ? "Not authorized to export." : "Export failed. Please try again." });
    } finally {
      setExporting(false);
    }
  };
  const counts = useMemo(() => {
    const c = { attending: 0, pending: 0, declined: 0 };
    rows.forEach((a) => { if (a.is_plus_one) return; const r = a.rsvp_canonical || ""; if (c[r] != null) c[r] += 1; });
    return c;
  }, [rows]);

  const load = () => {
    if (!eventId) return;
    setLoadState("loading");
    api.adminAttendees(eventId)
      .then((data) => { setRows(Array.isArray(data) ? data : []); setLoadState("ok"); })
      .catch(() => setLoadState("error"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const filtered = useMemo(() => {
    return rows.filter((a) => {
      if (a.is_plus_one) return false; // show primaries in table; plus-ones shown in detail
      if (q) {
        const s = `${a.full_name} ${a.email || ""} ${a.employee_id || ""}`.toLowerCase();
        if (!s.includes(q.toLowerCase())) return false;
      }
      if (group !== "all" && !(a.groups || []).some((g) => g.id === group)) return false;
      if (rsvp !== "all" && (a.rsvp_canonical || "") !== rsvp) return false;
      if (tshirt !== "all") {
        const sz = tshirtKey(a.t_shirt_size);
        if (sz !== tshirt) return false;
      }
      if (plus !== "all") {
        const hasPlus = !!a.has_guest;
        if (plus === "yes" && !hasPlus) return false;
        if (plus === "none" && hasPlus) return false;
      }
      return true;
    });
  }, [rows, q, group, rsvp, plus, tshirt]);

  return (
    <div>
      <PageIntro>Search and filter attendees. Open an attendee to manage participation, preferences and individual session eligibility overrides.</PageIntro>

      {loadState === "error" && (
        <div data-testid="admin-attendees-error" className="premium-card p-6 text-center">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">We couldn't load the attendee list. Please try again.</p>
          <button data-testid="admin-attendees-retry" onClick={load} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--secondary))] text-white px-4 py-2 text-sm font-semibold hover:brightness-110 transition-[filter]">
            Try again
          </button>
        </div>
      )}

      {loadState !== "error" && (<>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <Input data-testid="admin-attendees-search" placeholder="Search name, email, employee ID" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white" />
        </div>
        <Select testid="admin-attendees-filter-group" value={group} onChange={setGroup} opts={GROUP_OPTS} />
        <Select testid="admin-attendees-filter-rsvp" value={rsvp} onChange={setRsvp} opts={RSVP_OPTS} />
        <Select testid="admin-attendees-filter-plusone" value={plus} onChange={setPlus} opts={PLUS_OPTS} />
        <button data-testid="admin-export-roster-btn" onClick={doExport} disabled={exporting} className="inline-flex items-center gap-1.5 rounded-xl border border-[hsl(var(--border))] bg-white text-[hsl(var(--primary))] px-4 py-2 text-sm font-semibold hover:bg-[hsl(var(--muted)/0.5)] disabled:opacity-50 transition-colors">
          <Download className="h-4 w-4" /> {exporting ? "Exporting…" : "Export Roster"}
        </button>
        <button data-testid="admin-add-attendee-btn" onClick={() => setAddOpen(true)} className="inline-flex items-center gap-1.5 rounded-xl bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90">+ Add Attendee</button>
      </div>

      {exportInfo && (
        <div data-testid="admin-export-status" className={`mb-4 rounded-xl border px-3 py-2 text-xs ${exportInfo.error ? "border-[hsl(0_60%_75%)] bg-[hsl(0_70%_97%)] text-[hsl(0_55%_40%)]" : "border-[hsl(var(--mint))] bg-[hsl(var(--mint))] text-[hsl(var(--primary))]"}`}>
          {exportInfo.error
            ? exportInfo.error
            : <>Exported <strong data-testid="admin-export-count">{exportInfo.count}</strong> records (all statuses) to <strong>{exportInfo.filename}</strong> · {new Date(exportInfo.ts).toLocaleString()}</>}
        </div>
      )}

      {/* RSVP filter counts */}
      <div className="flex flex-wrap gap-2 mb-4 text-xs" data-testid="rsvp-counts">
        {[["all", "All", rows.filter((a) => !a.is_plus_one).length], ["attending", "Attending", counts.attending], ["pending", "Pending", counts.pending], ["declined", "Declined", counts.declined]].map(([key, label, n]) => (
          <button key={key} data-testid={`rsvp-count-${key}`} onClick={() => setRsvp(key)} className={`px-3 py-1.5 rounded-full border font-semibold ${rsvp === key ? "bg-[hsl(var(--secondary)/0.15)] border-[hsl(var(--secondary))] text-[hsl(var(--secondary))]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>{label} — {n}</button>
        ))}
      </div>

      <TshirtSummary rows={rows} active={tshirt} onPick={setTshirt} />

      <div data-testid="admin-attendees-table" className="premium-card overflow-hidden">
        <div className="hidden lg:grid grid-cols-[2.4fr_1.6fr_1fr_1.5fr_0.9fr] gap-4 px-5 py-3 border-b border-[hsl(var(--border))] text-[11px] uppercase tracking-wide font-semibold text-[hsl(var(--muted-foreground))]">
          <div>Name</div><div>Group</div><div>RSVP</div><div>Arrival → Departure</div><div>Plus One</div>
        </div>
        <div className="divide-y divide-[hsl(var(--border))]">
          {filtered.map((a) => (
            <button key={a.id} data-testid="admin-attendees-row" onClick={() => setSelected(a.id)} className="w-full text-left grid grid-cols-1 lg:grid-cols-[2.4fr_1.6fr_1fr_1.5fr_0.9fr] gap-2 lg:gap-4 px-5 py-3 items-center hover:bg-[hsl(var(--muted)/0.5)]">
              <div className="flex items-center gap-3">
                {a.avatar ? <img src={a.avatar} alt="" className="h-9 w-9 rounded-full object-cover" /> : <div className="h-9 w-9 rounded-full bg-[hsl(var(--secondary))] text-white grid place-items-center text-xs font-semibold">{initials(a.full_name)}</div>}
                <div>
                  <div className="font-semibold text-[hsl(var(--primary))] flex items-center gap-1.5">{a.full_name}{a.vip_status && <Star className="h-3.5 w-3.5 text-[hsl(var(--pc-gold))] fill-[hsl(var(--pc-gold))]" />}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{a.email || a.employee_id}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">{a.groups?.map((g) => <GroupBadge key={g.id} groupId={g.id} groupName={g.name} size="xs" />)}</div>
              <div><RsvpPill status={a.rsvp_canonical} /></div>
              <div className="text-sm text-[hsl(var(--muted-foreground))]">{a.stay?.arrival ? `${a.stay.arrival.slice(5)} → ${a.stay.departure.slice(5)}` : "—"}</div>
              <div className="text-xs">{a.has_guest ? <span className="inline-flex items-center gap-1 text-[hsl(var(--secondary))] font-semibold"><UserPlus className="h-3.5 w-3.5" /> Yes</span> : <span className="text-[hsl(var(--muted-foreground))]">—</span>}</div>
            </button>
          ))}
          {filtered.length === 0 && <div className="px-5 py-10 text-center text-sm text-[hsl(var(--muted-foreground))]">No attendees match your filters.</div>}
        </div>
      </div>
      </>)}

      <AttendeeDrawer eventId={eventId} attendeeId={selected} onClose={() => setSelected(null)} onChanged={load} />
      {addOpen && <AddAttendeeModal eventId={eventId} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}
    </div>
  );
}

function AddAttendeeModal({ eventId, onClose, onSaved }) {
  const [f, setF] = useState({ email: "", first_name: "", last_name: "", preferred_name: "", attendee_type: "Non-PC Attendee", recognition: "None", rsvp_status: "pending" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.email.trim() || !f.email.includes("@")) return toast.error("Valid email required");
    if (!f.first_name.trim() && !f.last_name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const r = await api.mgrAddAttendee(eventId, f);
      toast.success(r.matched ? "Matched existing attendee (no duplicate created)" : "Attendee added");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    setBusy(false);
  };
  const I = (label, k, type = "text") => (
    <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">{label}</label>
      <input data-testid={`add-att-${k}`} type={type} value={f[k]} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm" /></div>
  );
  return (
    <div className="fixed inset-0 z-[130] grid place-items-center bg-black/50 p-4" data-testid="add-attendee-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3"><h3 className="font-serif text-lg text-[hsl(var(--primary))]">Add Attendee</h3><button data-testid="add-att-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-[hsl(var(--muted))]"><X className="h-4 w-4" /></button></div>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Manually added attendees are matched to a later Travel Master by email — never duplicated.</p>
        <div className="grid grid-cols-2 gap-3">{I("Email", "email")}{I("Preferred name", "preferred_name")}{I("First name", "first_name")}{I("Last name", "last_name")}</div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">RSVP</label>
            <select data-testid="add-att-rsvp" value={f.rsvp_status} onChange={(e) => set("rsvp_status", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm bg-white"><option value="pending">Pending</option><option value="attending">Attending</option><option value="declined">Declined</option></select></div>
          <div><label className="text-xs font-semibold text-[hsl(var(--primary))]">Recognition</label>
            <select data-testid="add-att-recognition" value={f.recognition} onChange={(e) => set("recognition", e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm bg-white">{["None", "MDC Winner", "PC Winner", "Sprint to PC Winner", "Upcoming Talent Winner", "Milestone Achiever"].map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">Cancel</button><button data-testid="add-att-submit" disabled={busy} onClick={submit} className="px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">Add</button></div>
      </div>
    </div>
  );
}

function TshirtSummary({ rows, active, onPick }) {
  const [open, setOpen] = useState(true);
  const data = useMemo(() => {
    const base = {};
    TSHIRT_ORDER.forEach((s) => { base[s] = { total: 0, attending: 0, pending: 0, declined: 0 }; });
    let total = 0;
    rows.forEach((a) => {
      if (a.is_plus_one) return;
      const sz = tshirtKey(a.t_shirt_size);
      const rv = a.rsvp_canonical || "pending";
      base[sz].total += 1;
      if (base[sz][rv] != null) base[sz][rv] += 1;
      total += 1;
    });
    return { base, total };
  }, [rows]);
  const LABEL = { MISSING: "Missing size", NA: "NA" };
  return (
    <div className="premium-card p-4 mb-4" data-testid="tshirt-order-summary">
      <div className="flex items-center gap-2 mb-1">
        <Shirt className="h-4 w-4 text-[hsl(var(--secondary))]" />
        <h3 className="font-semibold text-[hsl(var(--primary))]">T-Shirt Order Summary</h3>
        <span className="ml-2 text-[10px] uppercase tracking-wide rounded-full bg-[hsl(var(--pc-gold)/0.15)] text-[hsl(38_50%_34%)] px-2 py-0.5 font-semibold">Manager only</span>
        <button onClick={() => setOpen((o) => !o)} data-testid="tshirt-summary-toggle" className="ml-auto text-xs font-semibold text-[hsl(var(--secondary))]">{open ? "Hide" : "Show"}</button>
      </div>
      <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-3">Counts are calculated live from the roster. Click a size to filter the list below. Each card shows the RSVP split (Attending / Pending / Declined).</p>
      {open && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="tshirt-summary-grid">
          {TSHIRT_ORDER.map((sz) => {
            const d = data.base[sz];
            const isActive = active === sz;
            return (
              <button
                key={sz}
                data-testid={`tshirt-chip-${sz}`}
                onClick={() => onPick(isActive ? "all" : sz)}
                className={`text-left rounded-xl border px-3 py-2 transition-[background,border-color] ${isActive ? "border-[hsl(var(--secondary))] bg-[hsl(var(--secondary)/0.1)]" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted)/0.5)]"}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-xs font-semibold text-[hsl(var(--primary))]">{LABEL[sz] || sz}</span>
                  <span data-testid={`tshirt-count-${sz}`} className="text-lg font-serif text-[hsl(var(--primary))]">{d.total}</span>
                </div>
                <div className="mt-1 flex flex-wrap gap-1 text-[10px] text-[hsl(var(--muted-foreground))]">
                  <span className="text-[hsl(var(--secondary))] font-medium">A {d.attending}</span>
                  <span>· P {d.pending}</span>
                  <span>· D {d.declined}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {active !== "all" && (
        <button data-testid="tshirt-clear-filter" onClick={() => onPick("all")} className="mt-3 text-xs font-semibold text-[hsl(var(--secondary))]">Clear T-shirt filter ({LABEL[active] || active})</button>
      )}
    </div>
  );
}

function Select({ value, onChange, opts, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-lg border border-[hsl(var(--border))] bg-white px-3 py-2 text-sm font-medium text-[hsl(var(--primary))]">
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function RsvpPill({ status }) {
  const map = { attending: "bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))]", pending: "bg-[hsl(var(--pc-gold)/0.14)] text-[hsl(38_50%_34%)]", declined: "bg-[hsl(0_60%_92%)] text-[hsl(0_60%_40%)]" };
  const label = { attending: "Attending", pending: "Pending", declined: "Declined" };
  return <span data-testid="admin-attendee-rsvp-pill" className={`text-xs rounded-full px-2.5 py-1 font-medium ${map[status] || "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{label[status] || "—"}</span>;
}

function RecognitionOverlay({ eventId, attendeeId, onChanged }) {
  const RECS = ["None", "MDC Winner", "PC Winner", "Sprint to PC Winner", "Upcoming Talent Winner", "Milestone Achiever"];
  const [cls, setCls] = useState(null);
  const [val, setVal] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (attendeeId) api.mgrClassGet(eventId, attendeeId).then((c) => { setCls(c); setVal(c.effective.recognition); }).catch(() => {});
  }, [attendeeId, eventId]);
  if (!cls) return null;
  const src = cls.source?.recognition;
  const overridden = cls.override?.overrides?.recognition;
  const save = async () => {
    if (val === cls.effective.recognition) { toast.info("No change to save"); return; }
    if (!reason.trim()) { toast.error("A short reason is required"); return; }
    setSaving(true);
    try {
      await api.mgrClassSet(eventId, attendeeId, { recognition: val, reason });
      toast.success("Recognition overlay saved");
      setReason("");
      onChanged && onChanged();
      const c = await api.mgrClassGet(eventId, attendeeId);
      setCls(c); setVal(c.effective.recognition);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update recognition"); }
    finally { setSaving(false); }
  };
  return (
    <Section title="Recognition (Events-managed)" icon={Award}>
      <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Source (Travel Master): <b className="text-[hsl(var(--primary))]">{src}</b>{overridden && <span className="ml-1 text-[hsl(var(--pc-gold))] font-semibold">· overlay active</span>}</div>
      <select data-testid="recognition-overlay-select" value={val} onChange={(e) => setVal(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm bg-white">
        {RECS.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <input data-testid="recognition-overlay-reason" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-2 w-full px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm bg-white" />
      <button data-testid="recognition-overlay-save" onClick={save} disabled={saving} className="mt-2 w-full rounded-lg bg-[hsl(var(--primary))] text-white py-2 text-sm font-semibold hover:brightness-110 disabled:opacity-50">{saving ? "Saving…" : "Save recognition"}</button>
      <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1.5">Travel Master source data is never changed — this is an event-scoped, audited overlay.</div>
    </Section>
  );
}

function AttendeeDrawer({ eventId, attendeeId, onClose, onChanged }) {  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();

  const load = () => { if (attendeeId) api.attendeeDetail(eventId, attendeeId).then(setDetail); };
  useEffect(() => { setDetail(null); load(); /* eslint-disable-next-line */ }, [attendeeId, eventId]);

  const toggle = async (e) => {
    try {
      if (e.override) {
        await api.removeOverride(eventId, attendeeId, e.session_id);
        toast.success("Override removed");
      } else {
        const decision = e.status === "included" ? "deny" : "allow";
        await api.addOverride(eventId, { attendee_id: attendeeId, session_id: e.session_id, decision });
        toast.success(`Individual ${decision.toUpperCase()} added`);
      }
      load();
      onChanged && onChanged();
    } catch (err) { toast.error("Could not update override"); }
  };

  const a = detail?.attendee;
  return (
    <Sheet open={!!attendeeId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="admin-attendee-detail-drawer" side="right" className="w-full sm:max-w-lg overflow-y-auto no-scrollbar p-0 bg-[hsl(var(--background))]">
        {!detail ? (
          <div className="p-6 space-y-3"><div className="h-20 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" /><div className="h-40 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" /></div>
        ) : (
          <div className="p-5 space-y-5">
            {/* header */}
            <div className="flex items-center gap-3">
              {a.avatar ? <img src={a.avatar} alt="" className="h-14 w-14 rounded-full object-cover" /> : <div className="h-14 w-14 rounded-full bg-[hsl(var(--secondary))] text-white grid place-items-center font-semibold">{initials(a.full_name)}</div>}
              <div>
                <div className="font-serif text-2xl text-[hsl(var(--primary))] flex items-center gap-2">{a.full_name}{a.vip_status && <Star className="h-4 w-4 text-[hsl(var(--pc-gold))] fill-[hsl(var(--pc-gold))]" />}</div>
                <div className="flex flex-wrap gap-1.5 mt-1">{a.groups?.map((g) => <GroupBadge key={g.id} groupId={g.id} groupName={g.name} size="xs" />)}</div>
              </div>
            </div>

            {!a.is_plus_one && (
              <button
                data-testid="admin-attendee-preview-btn"
                onClick={() => navigate(`/manager/attendees/${attendeeId}/preview`)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[hsl(var(--pc-gold))] px-4 py-2.5 text-sm font-semibold text-[hsl(var(--pc-ink))] hover:opacity-90 transition-opacity"
              >
                <Eye className="h-4 w-4" /> Preview attendee experience
              </button>
            )}

            <Section title="Profile" icon={ShieldCheck}>
              <Field k="Employee ID" v={a.employee_id} />
              <Field k="Email" v={a.email} />
              <Field k="Title" v={a.job_title} />
              <Field k="Business Unit" v={a.business_unit} />
            </Section>

            <Section title="Event Participation" icon={CalendarCheck}>
              <Field k="RSVP" v={a.rsvp_display} />
              <Field k="Participation" v={a.participation_status} />
              <Field k="Arrival" v={a.stay?.arrival} />
              <Field k="Departure" v={`${a.stay?.departure || "—"} (${a.stay?.nights ?? "—"} nights)`} />
              <Field k="Stay source" v={a.stay?.source === "override" ? "Individual override" : "Group default"} />
              <Field k="VIP" v={a.vip_status ? "Yes" : "No"} />
            </Section>

            <Section title="Operational (Manager only)" icon={Shirt}>
              <Field k="Attendee Key" v={a.attendee_key} />
              <div className="flex justify-between gap-4 text-sm">
                <dt className="text-[hsl(var(--muted-foreground))] shrink-0">T-Shirt Size</dt>
                <dd data-testid="admin-attendee-tshirt" className="font-medium text-[hsl(var(--primary))] text-right">
                  {a.t_shirt_size ? <span className="inline-block rounded-full bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))] px-2.5 py-0.5 text-xs font-semibold">{a.t_shirt_size}</span> : <span className="text-[hsl(var(--muted-foreground))]">Not set</span>}
                </dd>
              </div>
              <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Operational only — never shown in the attendee app.</div>
            </Section>

            <ManagerAccommodation accom={detail.accommodation} />

            <RecognitionOverlay eventId={eventId} attendeeId={attendeeId} onChanged={() => { load(); onChanged && onChanged(); }} />

            <Section title="Plus One" icon={UserPlus}>
              {detail.plus_one ? (
                <>
                  <Field k="Guest" v={`${detail.plus_one.first_name} ${detail.plus_one.last_name}`} />
                  <Field k="Relationship" v={detail.plus_one.relationship || detail.plus_one.relationship_to_primary} />
                  <Field k="Dietary" v={detail.plus_one.dietary_preference} />
                  <div className="flex gap-2 mt-1">
                    <Cover ok={detail.plus_one.travel_covered} label="Travel" icon={Plane} />
                    <Cover ok={detail.plus_one.hotel_covered} label="Hotel" icon={BedDouble} />
                    <Cover ok={detail.plus_one.transfer_covered} label="Transfer" icon={Car} />
                  </div>
                </>
              ) : detail.guest ? (
                <div data-testid="admin-attendee-guest">
                  <Field k="Plus One" v="Yes" />
                  <Field k="Guest" v={detail.guest.name} />
                  <Field k="Relationship" v={detail.guest.relationship} />
                  <Field k="Gala meal" v={detail.guest.gala_meal} />
                </div>
              ) : <div className="text-sm text-[hsl(var(--muted-foreground))]">No plus-one registered.</div>}
            </Section>

            <Section title="Preferences" icon={Utensils}>
              <Field k="Dietary" v={a.dietary_preference} />
              <Field k="Notes" v={a.special_dietary_notes || "—"} />
            </Section>

            {/* Eligibility + overrides */}
            <div>
              <div className="flex items-center gap-2 mb-2"><Plus className="h-4 w-4 text-[hsl(var(--secondary))]" /><h3 className="font-serif text-xl text-[hsl(var(--primary))]">Eligibility</h3></div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Toggle an individual ALLOW or DENY without changing the attendee's group.</p>
              <div data-testid="admin-attendee-eligibility-list" className="premium-card divide-y divide-[hsl(var(--border))]">
                {detail.eligibility.map((e) => (
                  <div key={e.session_id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[hsl(var(--primary))] truncate flex items-center gap-1.5">
                        {e.status === "included" ? <Check className="h-3.5 w-3.5 text-[hsl(var(--secondary))]" /> : <Ban className="h-3.5 w-3.5 text-[hsl(var(--muted-foreground))]" />}
                        {e.title}
                      </div>
                      <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{e.basis}{e.override ? ` · ${e.override.toUpperCase()} override` : ""}</div>
                    </div>
                    <button data-testid="admin-attendee-override-toggle" onClick={() => toggle(e)} className={`shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-semibold border ${e.override ? "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]" : e.status === "included" ? "border-[hsl(0_60%_80%)] text-[hsl(0_60%_45%)]" : "border-[hsl(var(--pc-gold)/0.5)] text-[hsl(38_50%_34%)] bg-[hsl(var(--pc-gold)/0.1)]"}`}>
                      {e.override ? "Remove" : e.status === "included" ? "Deny" : "Allow"}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Future placeholders */}
            <div>
              <h3 className="font-serif text-xl text-[hsl(var(--primary))] mb-2">Operational data</h3>
              <div className="grid grid-cols-2 gap-2">
                {[["Travel", Plane], ["Hotel", BedDouble], ["Transfers", Car], ["Appointments", CalendarCheck]].map(([l, Ic]) => (
                  <div key={l} className="rounded-xl border border-dashed border-[hsl(var(--border))] bg-white/50 p-3 text-center">
                    <Ic className="h-5 w-5 mx-auto text-[hsl(var(--muted-foreground))]" />
                    <div className="text-xs font-semibold text-[hsl(var(--primary))] mt-1">{l}</div>
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))]">Coming in a future phase</div>
                  </div>
                ))}
              </div>
            </div>

            {a.internal_notes && (
              <div className="rounded-xl bg-[hsl(var(--lavender)/0.4)] border border-[hsl(var(--lavender))] p-3">
                <div className="text-[11px] uppercase font-semibold text-[hsl(var(--primary))]">Internal notes (admin only)</div>
                <div className="text-sm text-[hsl(var(--primary))] mt-1">{a.internal_notes}</div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ManagerAccommodation({ accom }) {
  const [open, setOpen] = useState(false);
  if (!accom) return null;
  const t = accom.totals || {};
  const statusLabel = { calculated: "Calculated", pending: "Pending — stay dates not confirmed",
    not_applicable: "Not applicable (declined / cancelled)" }[accom.status] || accom.status;
  return (
    <div className="premium-card p-4" data-testid="admin-attendee-accommodation">
      <div className="flex items-center gap-2 mb-2">
        <Wallet className="h-4 w-4 text-[hsl(var(--secondary))]" />
        <h3 className="font-semibold text-[hsl(var(--primary))]">Accommodation</h3>
        <span className="ml-auto text-[10px] uppercase tracking-wide rounded-full bg-[hsl(var(--pc-gold)/0.15)] text-[hsl(38_50%_34%)] px-2 py-0.5 font-semibold">System calculated</span>
      </div>
      {accom.coverage_override === "company_operational" && (
        <div data-testid="admin-accommodation-company-covered" className="mb-3 rounded-lg border border-[hsl(var(--pc-gold)/0.4)] bg-[hsl(var(--pc-gold)/0.1)] px-3 py-2">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-[hsl(38_50%_30%)]">
            <ShieldCheck className="h-4 w-4" /> Coverage: Company Covered
          </div>
          <div className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))]">Reason: {accom.coverage_reason || "Event Operations / On-ground Support"}</div>
          <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">Approved operational extension — stay is longer than the standard entitlement and is fully company-covered (not a personal extension).</div>
        </div>
      )}
      <dl className="space-y-1.5">
        <Field k="Category" v={accom.category} />
        <Field k="Plus-one" v={accom.has_plus_one ? "Yes" : "No"} />
        <Field k="Planned check-in" v={accom.checkin} />
        <Field k="Planned check-out" v={accom.checkout} />
        <Field k="Total nights" v={String(accom.total_nights ?? "—")} />
        <Field k="Covered nights" v={String(accom.covered_nights ?? "—")} />
        <Field k="Personal extension nights" v={String(accom.extension_nights ?? "—")} />
        <Field k="Company accommodation cost" v={t.company_display} />
        <Field k="Employee accommodation cost" v={t.employee_display} />
        <Field k="Payment method" v="Direct to Hotel" />
        <Field k="Calculation status" v={statusLabel} />
      </dl>
      {accom.nights?.length > 0 && (
        <>
          <button onClick={() => setOpen((o) => !o)} data-testid="admin-attendee-accommodation-breakdown-toggle"
            className="mt-2 text-xs font-semibold text-[hsl(var(--secondary))]">
            {open ? "Hide night-by-night breakdown" : "View night-by-night breakdown"}
          </button>
          {open && (
            <div className="mt-2 divide-y divide-[hsl(var(--border))]" data-testid="admin-attendee-accommodation-breakdown">
              {accom.nights.map((n) => (
                <div key={n.date} className="flex items-center justify-between py-1.5 text-xs">
                  <span className="text-[hsl(var(--primary))]">{n.date}</span>
                  <span className="text-[hsl(var(--muted-foreground))]">{n.night_type} · {n.occupancy}</span>
                  <span className="text-[hsl(var(--primary))] font-medium">Co {n.company_amount} / Emp {n.employee_amount}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}


function Section({ title, icon: Icon, children }) {
  return (
    <div className="premium-card p-4">
      <div className="flex items-center gap-2 mb-2"><Icon className="h-4 w-4 text-[hsl(var(--secondary))]" /><h3 className="font-semibold text-[hsl(var(--primary))]">{title}</h3></div>
      <dl className="space-y-1.5">{children}</dl>
    </div>
  );
}
function Field({ k, v }) {
  return <div className="flex justify-between gap-4 text-sm"><dt className="text-[hsl(var(--muted-foreground))] shrink-0">{k}</dt><dd className="font-medium text-[hsl(var(--primary))] text-right capitalize">{v || "—"}</dd></div>;
}
function Cover({ ok, label, icon: Icon }) {
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium ${ok ? "bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))]" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}><Icon className="h-3 w-3" /> {label}{ok ? "" : " ✕"}</span>;
}
